import * as React from 'react';

import { showAxiosError, showGlobalError } from 'vms/app/AppToaster';
import { BadRequestError } from 'vms/common/utils';

import AppDialog from './AppDialog';
import type {
  DialogCode,
  DialogData,
  DialogErrors,
  DialogState,
  EditableData,
} from './types';

interface DialogContextValue {
  startDialog<A extends { dialogCode: DialogCode }>(
    payload: A,
    loadData: (payload: A) => Promise<DialogData | undefined>,
    saveData: (data: DialogData) => Promise<void>
  ): Promise<void>;
}

interface DialogSession {
  busy: boolean;
  saveData: (data: DialogData) => Promise<void>;
}

const initialState: DialogState = {
  code: null,
  editableData: null,
  readOnlyData: null,
  errors: {},
  readyToRender: false,
  isInProgress: false,
};

const DialogContext = React.createContext<DialogContextValue | null>(null);

export function DialogProvider({ children }: { children: React.ReactNode }) {
  const [dialog, setDialog] = React.useState(initialState);
  const sessionRef = React.useRef<DialogSession | null>(null);

  React.useEffect(
    () => () => {
      sessionRef.current = null;
    },
    []
  );

  const closeDialog = React.useCallback(() => {
    sessionRef.current = null;
    setDialog(initialState);
  }, []);

  const startDialog = React.useCallback<DialogContextValue['startDialog']>(
    async (payload, loadData, saveData) => {
      if (sessionRef.current) {
        return;
      }
      const code = payload.dialogCode;
      if (code === null) {
        throw new Error(`Unknown dialog code ${code}`);
      }
      const session = { busy: true, saveData };
      sessionRef.current = session;
      setDialog({ ...initialState, code });
      let data: DialogData | undefined;
      try {
        data = await loadData(payload);
      } catch {
        showGlobalError();
      } finally {
        session.busy = false;
      }
      if (sessionRef.current !== session) {
        return;
      }
      if (data === undefined) {
        closeDialog();
        return;
      }
      setDialog({ ...initialState, ...data, readyToRender: true, code });
    },
    [closeDialog]
  );

  const cancelDialog = React.useCallback(() => {
    if (sessionRef.current?.busy) {
      return;
    }
    closeDialog();
  }, [closeDialog]);

  const finishDialog = async () => {
    const session = sessionRef.current;
    const { readyToRender, editableData, readOnlyData } = dialog;
    if (!session || session.busy || !readyToRender || !editableData || !readOnlyData) {
      return;
    }
    session.busy = true;
    setDialog((current) => ({ ...current, isInProgress: true }));
    try {
      await session.saveData({ editableData, readOnlyData });
    } catch (e) {
      if (sessionRef.current !== session) {
        return;
      }
      if (e instanceof BadRequestError) {
        setDialog((current) => ({ ...current, errors: e.data }));
        showAxiosError(e.originalError);
      } else {
        showGlobalError();
        // oxlint-disable-next-line no-console
        console.error(e);
      }
      return;
    } finally {
      session.busy = false;
      if (sessionRef.current === session) {
        setDialog((current) => ({ ...current, isInProgress: false }));
      }
    }
    if (sessionRef.current === session) {
      closeDialog();
    }
  };

  const changeDialogData = (data: Partial<EditableData>) => {
    setDialog((current) => ({
      ...current,
      editableData: { ...current.editableData, ...data } as EditableData,
    }));
  };

  const setErrors = (errors: DialogErrors) => {
    setDialog((current) => ({ ...current, errors }));
  };

  const value = React.useMemo(() => ({ startDialog }), [startDialog]);

  return (
    <DialogContext.Provider value={value}>
      {children}
      <AppDialog
        {...dialog}
        cancelDialog={cancelDialog}
        finishDialog={finishDialog}
        changeDialogData={changeDialogData}
        setErrors={setErrors}
      />
    </DialogContext.Provider>
  );
}

export function useDialog() {
  const context = React.useContext(DialogContext);
  if (!context) {
    throw new Error('useDialog must be used within DialogProvider');
  }
  return context;
}
